import * as RadixMenu from '@radix-ui/react-dropdown-menu';
import type { LabelDto } from '@tms/shared';
import { Check, ChevronDown, Plus, Tag } from 'lucide-react';
import { useState, type KeyboardEvent } from 'react';
import { toast } from 'sonner';
import { Input, LabelChip, LabelDot, Spinner } from '../../components/ui';
import { useErrorText, useT } from '../../i18n';
import { cn } from '../../lib/cn';
import { usePermissions } from '../auth/permissions';
import { useCreateLabel, useLabels } from './api';

export interface LabelPickerProps {
  projectCode: string;
  value: string[];
  onChange: (labelIds: string[]) => void;
  id?: string;
}

/**
 * Multi-select for the labels of a test case or a checklist, with creation in
 * place: typing a
 * name that does not exist yet and confirming it adds the label to the project
 * and to what is being written in one step, so documenting does not have to
 * pause for a trip to the project settings.
 */
export const LabelPicker = ({ projectCode, value, onChange, id }: LabelPickerProps) => {
  const { t } = useT();
  const { can } = usePermissions();
  const { errorText } = useErrorText();
  const { data: labels } = useLabels(projectCode);
  const createLabel = useCreateLabel(projectCode);
  const [draft, setDraft] = useState('');

  const available = labels ?? [];
  const selected = available.filter((label) => value.includes(label.id));
  const canCreate = can('label:manage');

  const toggle = (labelId: string): void => {
    onChange(
      value.includes(labelId) ? value.filter((id) => id !== labelId) : [...value, labelId],
    );
  };

  const trimmed = draft.trim();
  const alreadyExists = available.some(
    (label) => label.name.toLowerCase() === trimmed.toLowerCase(),
  );

  const create = async (): Promise<void> => {
    if (trimmed.length === 0 || createLabel.isPending) return;
    const existing = available.find(
      (label) => label.name.toLowerCase() === trimmed.toLowerCase(),
    );
    if (existing) {
      if (!value.includes(existing.id)) toggle(existing.id);
      setDraft('');
      return;
    }

    try {
      const created = await createLabel.mutateAsync({ name: trimmed, color: 'gray' });
      onChange([...value, created.id]);
      setDraft('');
    } catch (error) {
      toast.error(errorText(error, 'labels.createFailed'));
    }
  };

  const onDraftKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    // The menu would otherwise treat typing as its own type-ahead navigation.
    event.stopPropagation();
    if (event.key === 'Enter') {
      event.preventDefault();
      void create();
    }
  };

  return (
    <RadixMenu.Root>
      <RadixMenu.Trigger asChild>
        <button
          id={id}
          type="button"
          className={cn(
            'flex min-h-9 w-full items-center gap-2 rounded-md border border-border bg-surface px-2.5 py-1',
            'text-left text-[14px] text-ink transition-colors hover:border-border-strong',
          )}
        >
          <Tag className="size-3.5 shrink-0 text-ink-subtle" />
          {selected.length === 0 ? (
            <span className="text-ink-subtle">{t('labels.none')}</span>
          ) : (
            <span className="flex flex-wrap items-center gap-1">
              {selected.map((label) => (
                <LabelChip key={label.id} label={label} />
              ))}
            </span>
          )}
          <ChevronDown className="ml-auto size-4 shrink-0 text-ink-subtle" />
        </button>
      </RadixMenu.Trigger>

      <RadixMenu.Portal>
        <RadixMenu.Content
          align="start"
          side="bottom"
          sideOffset={4}
          collisionPadding={8}
          className="z-50 max-h-80 w-64 overflow-y-auto rounded-lg border border-border bg-surface p-1 shadow-lg"
        >
          {available.length === 0 ? (
            <p className="px-2 py-1.5 text-[12px] text-ink-subtle">{t('labels.empty')}</p>
          ) : (
            available.map((label: LabelDto) => (
              <RadixMenu.CheckboxItem
                key={label.id}
                checked={value.includes(label.id)}
                onCheckedChange={() => toggle(label.id)}
                onSelect={(event) => event.preventDefault()}
                className={cn(
                  'relative flex cursor-pointer items-center gap-2 rounded-md py-1.5 pl-7 pr-2',
                  'text-[13px] text-ink outline-none data-[highlighted]:bg-sunken',
                )}
              >
                <RadixMenu.ItemIndicator className="absolute left-2 text-accent-fg">
                  <Check className="size-3.5" />
                </RadixMenu.ItemIndicator>
                <LabelDot color={label.color} />
                <span className="truncate">{label.name}</span>
                <span className="ml-auto text-[11px] tabular-nums text-ink-subtle">
                  {/* Everything wearing the label: the picker serves both test
                      cases and checklists. */}
                  {label.caseCount + label.checklistCount}
                </span>
              </RadixMenu.CheckboxItem>
            ))
          )}

          {canCreate ? (
            <>
              <RadixMenu.Separator className="my-1 h-px bg-border" />
              <div className="flex items-center gap-1 p-1">
                <Input
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={onDraftKeyDown}
                  placeholder={t('labels.newPlaceholder')}
                  className="h-8 text-[13px]"
                  aria-label={t('labels.new')}
                />
                <button
                  type="button"
                  onClick={() => void create()}
                  disabled={trimmed.length === 0 || createLabel.isPending}
                  aria-label={t('labels.add')}
                  title={alreadyExists ? t('labels.alreadyExists') : t('labels.add')}
                  className={cn(
                    'inline-flex size-8 shrink-0 items-center justify-center rounded-md',
                    'text-ink-subtle hover:bg-sunken hover:text-ink disabled:opacity-40',
                  )}
                >
                  {createLabel.isPending ? (
                    <Spinner className="size-3.5" />
                  ) : (
                    <Plus className="size-3.5" />
                  )}
                </button>
              </div>
            </>
          ) : null}
        </RadixMenu.Content>
      </RadixMenu.Portal>
    </RadixMenu.Root>
  );
};
